import { type FC } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useStreamEntrypointsListQuery,
  type StreamEntrypointsItem,
} from "@/partner/entities/streamEntrypoints";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { interpolationFormat } from "@/partner/shared/lib";
import { PagePaths } from "@/partner/shared/constants";

interface StreamEntrypointsListProps {
  onClickItem: (id: string) => void;
}

function StatusChip({ item }: { item: StreamEntrypointsItem }) {
  const { t } = useTranslation();

  return (
    <Chip
      size="small"
      color={item.liveConnected ? "success" : "default"}
      label={t(
        item.liveConnected
          ? "streamEntrypoints.status.online"
          : "streamEntrypoints.status.offline"
      )}
    />
  );
}

export const StreamEntrypointsList: FC<StreamEntrypointsListProps> = ({
  onClickItem,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();

  const { data, loading } = useStreamEntrypointsListQuery({
    variables: { page, rowsPerPage, search },
    fetchPolicy: "cache-and-network",
    // liveConnected/stream can change server-side (encoder connects/
    // disconnects, привязка меняется извне) without any local mutation to
    // trigger a refetch — poll while the list is mounted.
    pollInterval: 15000,
  });

  const nodes = data?.streamEntrypoints.nodes ?? [];
  const count = data?.streamEntrypoints.count ?? 0;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return <EmptyListCard title={t("streamEntrypoints.list.empty")} />;
  }

  return (
    <Card elevation={0}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("streamEntrypoints.columns.title")}</TableCell>
            {!isMobile && (
              <TableCell>{t("streamEntrypoints.columns.endpoint")}</TableCell>
            )}
            <TableCell>{t("streamEntrypoints.columns.status")}</TableCell>
            {!isMobile && (
              <TableCell>{t("streamEntrypoints.columns.stream")}</TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">
                {t("streamEntrypoints.columns.createdAt")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>
                <ItemTitleLink
                  to={`${PagePaths.streamEntrypoints}?id=${item.id}`}
                  onOpen={() => onClickItem(item.id)}
                  color="textPrimary"
                  variant="h6"
                >
                  {item.title}
                </ItemTitleLink>
              </TableCell>
              {!isMobile && (
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {item.rtmpsUrl}
                  </Typography>
                </TableCell>
              )}
              <TableCell>
                <StatusChip item={item} />
              </TableCell>
              {!isMobile && (
                <TableCell>
                  {item.stream?.title ?? t("streamEntrypoints.attach.none")}
                </TableCell>
              )}
              {!isMobile && (
                <TableCell align="right">
                  <Typography variant="body2" color="text.primary" noWrap>
                    {interpolationFormat(
                      item.createdAt,
                      "short",
                      i18n.language
                    )}
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
